/**
 * Native Shoes — Collection Card Swatches
 * ────────────────────────────────────────────────────────────────────────────
 * Author:   Anton Siedlecki
 * Version:  3.4.0
 *
 * v3.4.0 — Real, live-confirmed bug: variants(first:100).nodes[0]'s
 * price/compareAtPrice is NOT the same thing as Shopify's product.
 * price/compare_at_price. Confirmed on a real price-varying product
 * (Robbie Kids: kid sizes at $60, toddler sizes at $55) that nodes[0]
 * gave $60 while the correct, documented value ("price of the
 * product's cheapest variant") is $55. New getSiblingPriceRange() finds
 * the actual cheapest variant and uses THAT SAME variant's own
 * price/compareAtPrice together — one shared source of truth now used
 * by both getProductBadgeData() and getProductPriceData(). A first
 * attempt read the Product type's priceRange/compareAtPriceRange
 * fields instead (cleaner-looking), but this was reverted after
 * confirming LIVE it could produce a false "on sale" badge purely from
 * price varying by size — pairing the cheapest variant's price with
 * the highest compareAtPrice across ALL variants isn't guaranteed to
 * be the same variant (see assets/native-swatch-shared.js's matching
 * v1.4.0 changelog for the full live evidence). Confirmed this bug
 * (and its fix) is correctly scoped per colorway regardless of which
 * approach — each sibling color is its own separate Shopify product in
 * this catalog's consolidated_style architecture, so every GraphQL
 * Product node's own variants only ever reflect THAT color's own size
 * range, never mixed across colors.
 *
 * v3.3.0 — Price sync: same root cause as v3.2.0's badge fix — clicking
 * a sibling swatch never updated the card's price block (snippets/
 * price.liquid) either, only ever the image/shadow/link/badges. Added
 * formatMoney() (Shopify's own long-published money-formatting
 * algorithm, reimplemented here since this theme has no existing money-
 * format JS utility), getProductPriceData() (mirrors getProductImages()/
 * getProductBadgeData()'s current-vs-sibling split), and
 * updateCardPrice() (rebuilds .card-product--price's regular/sale/
 * compare-at text and the price--on-sale/price--no-compare classes,
 * matching price.liquid's exact card-context output — confirmed via
 * snippets/card-product.liquid's actual render call that a card never
 * gets use_variant/show_badges/show_discounts, so the discount-badge
 * and unit-price blocks don't apply to a card and aren't replicated).
 * New config read in readContainerData(): currPriceData (this card's
 * own price/compare_at_price/price_varies/compare_at_price_varies, all
 * Liquid-rendered) and moneyCfg (shop.money_format/money_with_currency_
 * format + settings.currency_code_enabled). See snippets/native-
 * collection-swatches.liquid's matching v4.4.0 changelog.
 *
 * v3.2.0 — Dynamic badging: handleSwatchClick() already swapped the
 * image and drop-shadow class for whichever sibling color got clicked,
 * but left .card-product--badges showing whatever the ORIGINAL server-
 * rendered color's Sale/tag/metafield badges were — a real, user-
 * reported bug (Jefferson: only a few colors in the style are actually
 * on sale, but the badge didn't track the color being previewed). Sold-
 * out badges aren't part of this fix — a sold-out color is filtered out
 * of the clickable swatch set entirely before render (processContainer()
 * already does this via availMap), so there's no stale sold-out state to
 * correct. Mirrors snippets/card-product.liquid's exact badge logic
 * (tag-derived "Badge:" spans, custom.badges metafield list, Sale badge
 * from compare_at_price truthiness) using the three new fields
 * assets/native-swatch-shared.js's v1.2.0 added to the shared GraphQL
 * shape (tags, badgesMetafield, variants[].compareAtPrice), plus new
 * data attributes on this card's own current-product data (see
 * snippets/native-collection-swatches.liquid's matching v4.3.0 entry)
 * for correctly restoring THIS color's own badges when re-selected.
 *
 * v3.1.2 — Fix: clicking a sibling swatch swapped in that color's main/
 * hover image using a width-only CDN URL (buildSizedUrl(url, 800)),
 * unlike this card's OWN image, which snippets/native-collection-
 * swatches.liquid already renders with the site-wide width+height+
 * crop=center standard (snippets/card-product-image.liquid). Sibling
 * images come from the GraphQL identity fetch as raw, untransformed
 * URLs, so this was a real, user-visible crop/aspect-ratio change on
 * click, not a display artifact. Fixed by having the Liquid snippet
 * emit data-card-img-height-ratio (this card's aspect_ratio, as height/
 * width) and threading it through buildSizedUrl()/buildSrcset() (now
 * both take a ratio and append &height=&crop=center) via
 * readContainerData() -> renderSwatches() -> handleSwatchClick().
 * cleanImageUrl() also now strips a pre-existing ?crop= param (the
 * current product's own image already has one baked in by Liquid)
 * alongside width/height, so re-selecting the current color doesn't
 * produce a URL with two conflicting crop params.
 *
 * v3.1.1 — Fix: fetchLiveAvailability() still called a bare gql(...) left
 * over from before the v3.1.0 extraction — that function no longer exists
 * in this file, so every boot() run threw a ReferenceError inside the
 * uncaught Promise.all(...).then(...) chain, and the skeleton never got
 * replaced with real swatches (PDP was unaffected — its own extraction
 * updated every call site). Fixed by routing through
 * window.NativeShops.swatchShared.gql(). Also added a .catch() to that
 * boot() chain (previously had none) so any future unexpected failure
 * hides the affected rows instead of leaving them stuck on the skeleton
 * forever, consistent with this feature's fail-closed guarantee.
 *
 * v3.1.0 — Cache/fetch logic (gql, style identity cache, swatch cache,
 * version-validated revalidation) extracted to assets/native-swatch-
 * shared.js (2026-08-28), shared with assets/native-product-cross-
 * links.js so a visit to either a collection page or a PDP for the same
 * consolidated_style warms the cache the other reads — explicit user
 * request, since both features were independently caching the exact
 * same per-style data under separate namespaces. This file now calls
 * window.NativeShops.swatchShared.getStyleIdentity()/fetchSwatches()
 * instead of its own local copies of those functions. Everything below
 * that's specific to THIS feature is unchanged: fetchLiveAvailability()
 * (product-level, always-live, render-blocking — collection grid's own
 * correctness guarantee, not shared, since PDP needs different
 * granularity), all rendering/click-handling, boot() orchestration. See
 * native-swatch-shared.js's own header for the full design and the
 * CRITICAL superset-shape rule that makes sharing safe.
 *
 * v3.0.0 — RESTORED from the pre-2026-08-25 Storefront API version
 * (~/Downloads/native-swatch-graphql-removal-BACKUP-2026-08-25/, that
 * file's own v2.8.0), replacing the pure-Liquid batched endpoint
 * (sections/native-swatch-data.liquid, now unused/deletable) that
 * replaced it in between. Explicit reason for coming back to GraphQL:
 * two different pure-Liquid attempts at "4 swatches + N more, no
 * separate fetch" were built and live-tested this same day — inlining
 * per-card into the main grid's own render, and the dedicated batched
 * endpoint before that — and BOTH are bounded by the same real, proven
 * Shopify Liquid per-render lookup ceiling (~24-25 metaobject/
 * all_products[] dereferences per page render, already found and fixed
 * once on this exact endpoint on 2026-08-26, and again independently on
 * the PDP cross-links this same day). A live test on a real 28-product
 * page inlining swatches directly into the grid's own render got real
 * data for only ~7 of 28 cards before that ceiling ran out. GraphQL has
 * no equivalent limit.
 *
 * Explicit correctness requirement driving the rest of this rewrite: a
 * color that sells out and gets pulled from sale must never keep
 * showing as a swatch just because cached data says otherwise — "the
 * cache needs to be perfect." The v2.8.0 base this restores did NOT
 * actually guarantee that: isFullyOOS() read variants.availableForSale
 * from the SAME 24-hour(-ish, previously 15-minute) cached blob as the
 * swatch images/names, and neither of its two revalidation triggers
 * (consolidated_style.updatedAt, the sibling handle set) changes when a
 * product simply goes out of stock or gets unpublished — inventory
 * flipping doesn't touch either signal. So a sold-out color could have
 * kept showing as a live option for the entire cache window.
 *
 * Fixed by splitting this feature into two genuinely separate data
 * sources with different lifetimes, mirroring the split already proven
 * on the PDP side (assets/native-product-cross-links.js) between cached
 * assortment data and live per-size availability:
 *   - Style IDENTITY (handles, colorMetafield, swatch image/hex/name) —
 *     cached 24 hours in localStorage, version-validated stale-while-
 *     revalidate (consolidated_style.updatedAt + sibling handle set),
 *     same pattern as before. This data changes rarely and isn't safety-
 *     critical if briefly stale.
 *   - AVAILABILITY (availableForSale per product) — NEVER cached, NEVER
 *     read from the identity fetch at all (removed the variants field
 *     from that query entirely). One fresh, batched, @inContext-scoped
 *     query fires on every page load / grid update, covering every
 *     product handle needed across every style shown on the page in ONE
 *     request (not one per style, not one per card) — this is what
 *     actually decides whether a color renders at all. A handle missing
 *     from a successful response, or a failed request, fails CLOSED
 *     (treated as unavailable, hidden) rather than open — showing a
 *     sold-out swatch is the worse failure mode here, unlike the size-
 *     availability check on PDP where failing open (no false slash) was
 *     the right call for a lower-stakes visual flag.
 *
 * Also changed from the restored v2.8.0:
 *   - localStorage instead of sessionStorage (a day-long cache is
 *     pointless if it's wiped on tab close), CACHE_TTL_MS raised from 15
 *     minutes to 24 hours to match.
 *   - Swatch metaobject data (image/hex/name) now ALSO persists across
 *     page loads (its own simple, flat-TTL localStorage cache keyed by
 *     color code, not tied to any one style) instead of being an
 *     in-memory-only fetch every time — the same color code is shared
 *     by many different products/styles, so this avoids re-fetching an
 *     identical swatch image URL over and over across different pages.
 *   - boot() now resolves identity for every distinct style on the page
 *     BEFORE firing the one shared live-availability request, and does
 *     not render ANY card until both are ready — no card-by-card
 *     staggering, no partial reveal, matching an explicit request that
 *     the whole row appear complete, all at once, or not at all yet.
 *
 * Note on "instant, no delay": self-only cards (no consolidated_style at
 * all) render immediately with zero network calls — this card is
 * already known available, or it wouldn't be on the page. For any card
 * with real siblings, though, genuinely live-checking availability on
 * every load means there is always at least one network round trip
 * before that row can render, on every page load, cache or no cache —
 * that is the actual, necessary cost of the "never show a sold-out
 * swatch" guarantee, not something a cache can eliminate. What the 24h
 * identity cache removes is the SLOW, heavy part (images, names, full
 * sibling lists) on repeat visits; the live check that remains is a
 * single lightweight batched request (just a boolean per handle).
 *
 * Everything below not called out above (click-to-swap image handling,
 * DOM snapshotting, URL/querystring helpers, drop-shadow toggle) is
 * unchanged from the restored v2.8.0 — see its own history for that.
 */
(function () {
  'use strict';

  var imagePrefetched = new Set();

  function debugEnabled() {
    try {
      if (new URLSearchParams(window.location.search).get('native_card_swatches_debug') === '1') return true;
      if (window.localStorage && window.localStorage.getItem('native_card_swatches_debug') === '1') return true;
    } catch (e) {}
    return false;
  }

  var DEBUG = debugEnabled();
  function log() { if (!DEBUG) return; console.log.apply(console, ['[card-swatches]'].concat(Array.prototype.slice.call(arguments))); }
  function warn() { console.warn.apply(console, ['[card-swatches]'].concat(Array.prototype.slice.call(arguments))); }
  function err() { console.error.apply(console, ['[card-swatches]'].concat(Array.prototype.slice.call(arguments))); }

  var SWATCH_LIMIT = 4;
  var COUNTRY_CODE = (window.NativeShops && window.NativeShops.config && window.NativeShops.config.countryCode) || 'ZZ';

  // Strip Shopify CDN size suffixes (_140x, _800x, etc.) from image
  // filenames before appending ?width= so the CDN actually resizes —
  // the filename suffix otherwise takes precedence over the query param.
  var SIZE_SUFFIX_RE = /_\d+x(\d+)?\./;

  function cleanImageUrl(url) {
    if (!url) return '';
    try {
      var u = new URL(url, window.location.href);
      u.pathname = u.pathname.replace(SIZE_SUFFIX_RE, '.');
      u.searchParams.delete('width');
      u.searchParams.delete('height');
      u.searchParams.delete('crop');
      return u.origin + u.pathname + (u.search ? u.search : '');
    } catch (e) {
      return url.split('?')[0].replace(SIZE_SUFFIX_RE, '.');
    }
  }

  // ratio is height/width, matching snippets/card-product-image.liquid's
  // own aspect_ratio-derived crop — passed through from data-card-img-
  // height-ratio so a sibling image swapped in on swatch click gets the
  // exact same width+height+crop=center transform as this card's own
  // (Liquid-rendered) image, instead of a width-only, uncropped request.
  function buildSizedUrl(url, width, ratio) {
    var clean = cleanImageUrl(url);
    // Feature 310: cleanImageUrl keeps the CDN's ?v= version, so the next
    // parameter must join with '&'. A second '?' made "v=123?width=800" one
    // value and the CDN never resized the image.
    var sep = clean.indexOf('?') > -1 ? '&' : '?';
    if (!ratio) return clean + sep + 'width=' + width;
    var height = Math.round(width * ratio);
    return clean + sep + 'width=' + width + '&height=' + height + '&crop=center';
  }

  function buildSrcset(url, ratio) {
    // Feature 310: same ladder as snippets/card-product-image.liquid so a swapped-in
    // color is right-sized too; the <img>'s own sizes attribute is left as rendered.
    // Width only (no height/crop): server crops blurred some images.
    return [200, 250, 300, 350, 400, 500, 600, 800, 1000].map(function (w) { return buildSizedUrl(url, w, 0) + ' ' + w + 'w'; }).join(', ');
  }

  // onlineStoreUrl is confirmed null for every product this store's
  // Storefront API token can see (see file history) — always falls
  // through to the plain handle-based path.
  function getProductPath(p) {
    if (p.onlineStoreUrl) {
      try { return new URL(p.onlineStoreUrl).pathname; } catch (e) {}
    }
    return '/products/' + p.handle;
  }

  function getCollectionQuery() {
    return window.location.search;
  }

  function buildSwatchUrl(productPath, collectionQuery) {
    if (!collectionQuery || collectionQuery === '?') return productPath;
    try {
      var qIdx = productPath.indexOf('?');
      var base   = qIdx !== -1 ? productPath.slice(0, qIdx) : productPath;
      var merged = new URLSearchParams(qIdx !== -1 ? productPath.slice(qIdx + 1) : '');
      new URLSearchParams(collectionQuery).forEach(function (val, key) {
        merged.set(key, val);
      });
      var qs = merged.toString();
      return base + (qs ? '?' + qs : '');
    } catch (e) {
      return productPath + collectionQuery;
    }
  }


  // ─── gql(), style identity cache (readStyleCache/writeStyleCache/
  // checkStyleVersion/revalidateStyleInBackground), and the per-color
  // swatch cache (readSwatchCache/writeSwatchCache/fetchSwatches) all
  // moved to assets/native-swatch-shared.js (2026-08-28) — shared with
  // assets/native-product-cross-links.js so a visit to either page type
  // warms the same cache the other reads. Called below as
  // window.NativeShops.swatchShared.getStyleIdentity()/fetchSwatches().
  // See that file's header for the full design and the superset-shape
  // rule that makes sharing safe. ─────────────────────────────────────


  // ─── Live availability — NEVER cached, batched across every handle needed
  // on the page in ONE request. A handle missing from a successful response,
  // or the whole request failing, fails CLOSED (unavailable/hidden) — the
  // explicit correctness requirement this rewrite exists for. ──────────────
  function fetchLiveAvailability(domain, token, handles) {
    if (!handles.length) return Promise.resolve({});
    var fields = handles.map(function (h, i) {
      return 'p' + i + ': product(handle: "' + h.replace(/"/g, '\\"') + '") { availableForSale }';
    }).join(' ');
    return window.NativeShops.swatchShared.gql(
      domain, token,
      'query NativeSwatchesLiveAvailability($country: CountryCode!) @inContext(country: $country) { ' + fields + ' }',
      { country: COUNTRY_CODE }
    ).then(function (data) {
      var map = {};
      handles.forEach(function (h, i) {
        var p = data && data.data && data.data['p' + i];
        map[h] = !!(p && p.availableForSale);
      });
      return map;
    }).catch(function (e) {
      err('live availability fetch failed — failing CLOSED (hiding all siblings in this batch)', e);
      var map = {};
      handles.forEach(function (h) { map[h] = false; });
      return map;
    });
  }

  function buildDot(p, swatchMap) {
    var colorHandle = p.colorMetafield && p.colorMetafield.value;
    var swatch = (colorHandle && swatchMap && swatchMap[colorHandle]) || {};
    var colorName = (swatch.color_name && swatch.color_name.value) || '';
    var colorHex = (swatch.color_hex && swatch.color_hex.value) || '';
    var imgNode = swatch.color_image && swatch.color_image.reference && swatch.color_image.reference.image;
    var imgUrl = (imgNode && imgNode.url) || '';
    var imgAlt = (imgNode && imgNode.altText) || colorName;

    var dot = document.createElement('span');
    dot.className = 'ncs-dot';

    if (imgUrl) {
      var img = document.createElement('img');
      img.src = buildSizedUrl(imgUrl, 40, 1);
      img.alt = imgAlt;
      img.width = 25;
      img.height = 25;
      img.setAttribute('loading', 'lazy');
      img.setAttribute('aria-hidden', 'true');
      dot.appendChild(img);
    } else if (colorHex) {
      dot.style.backgroundColor = colorHex;
    } else {
      var fi = p.featuredImage;
      if (fi && fi.url) {
        var img2 = document.createElement('img');
        img2.src = buildSizedUrl(fi.url, 25, 1);
        img2.alt = fi.altText || '';
        img2.width = 25;
        img2.height = 25;
        img2.setAttribute('loading', 'lazy');
        img2.setAttribute('aria-hidden', 'true');
        dot.appendChild(img2);
      }
    }
    return dot;
  }

  function getProductImages(p, currImg1, currAlt1, currImg2, currAlt2, currHandle, currHasDropShadow) {
    if (p.handle === currHandle) {
      return { img1: currImg1, alt1: currAlt1, img2: currImg2, alt2: currAlt2, hasDropShadow: currHasDropShadow };
    }
    var imgs = (p.media && p.media.nodes) || [];
    var img1 = p.featuredImage ? p.featuredImage.url : '';
    var alt1 = p.featuredImage ? (p.featuredImage.altText || '') : '';
    var img2 = '', alt2 = '';
    if (imgs.length >= 2 && imgs[1] && imgs[1].image) {
      img2 = imgs[1].image.url || '';
      alt2 = imgs[1].image.altText || '';
    }
    var hasDropShadow = !!(p.imageDropShadowMetafield && p.imageDropShadowMetafield.value === 'true');
    return { img1: img1, alt1: alt1, img2: img2, alt2: alt2, hasDropShadow: hasDropShadow };
  }

  // Native Shoes (2026-08-31): a sibling's price/compare_at_price for
  // badge and price-display purposes both. Real, live-confirmed bug
  // this replaced: variants(first:100).nodes[0] is NOT the same thing
  // as "the product's price" — on a real price-varying product (Robbie
  // Kids, kid sizes at $60, toddler sizes at $55), nodes[0] gave $60
  // while the correct value ("price of the product's cheapest variant,"
  // Shopify's own documented definition, matching product.price) is
  // $55. Finds the actual cheapest variant instead of assuming position
  // 0. compareAtPrice deliberately comes from THAT SAME variant, not an
  // independent min/max across every variant — a first attempt at this
  // fix used the Product type's priceRange/compareAtPriceRange fields
  // (cleaner-looking, but confirmed LIVE to be wrong: pairing the
  // cheapest variant's price with the highest compareAtPrice across
  // ALL variants produced a false "on sale" signal purely from price
  // varying by size, since those two aggregates aren't guaranteed to
  // come from the same variant — on this exact product, every
  // variant's own compareAtPrice mirrors its own price exactly, no
  // real markdown anywhere, yet the two ranges alone suggested a
  // $55-vs-$60 discount that doesn't exist). Per-variant pairing avoids
  // that failure mode entirely: a variant's own compareAtPrice can only
  // ever be compared against that same variant's own price.
  function getSiblingPriceRange(p) {
    var variants = (p.variants && p.variants.nodes) || [];
    var withPrice = variants.filter(function (v) { return v.price && !isNaN(parseFloat(v.price.amount)); });
    if (!withPrice.length) return { price: null, compareAtPrice: null, priceVaries: false, compareAtPriceVaries: false };

    var cheapest = withPrice[0];
    withPrice.forEach(function (v) {
      if (parseFloat(v.price.amount) < parseFloat(cheapest.price.amount)) cheapest = v;
    });

    var compareAtPrice = cheapest.compareAtPrice ? cheapest.compareAtPrice.amount : null;
    if (compareAtPrice != null && parseFloat(compareAtPrice) <= parseFloat(cheapest.price.amount)) compareAtPrice = null;

    var prices = withPrice.map(function (v) { return parseFloat(v.price.amount); });
    var priceVaries = Math.min.apply(null, prices) !== Math.max.apply(null, prices);
    var compareAtPrices = withPrice
      .filter(function (v) { return v.compareAtPrice && parseFloat(v.compareAtPrice.amount) > parseFloat(v.price.amount); })
      .map(function (v) { return parseFloat(v.compareAtPrice.amount); });
    var compareAtPriceVaries = compareAtPrices.length > 0 && Math.min.apply(null, compareAtPrices) !== Math.max.apply(null, compareAtPrices);

    return { price: cheapest.price.amount, compareAtPrice: compareAtPrice, priceVaries: priceVaries, compareAtPriceVaries: compareAtPriceVaries };
  }

  // Native Shoes (2026-08-31): dynamic badging, see this file's v3.2.0
  // changelog. Mirrors getProductImages()'s current-vs-sibling split —
  // the current product's own badge data already came down as Liquid-
  // rendered data attributes (readContainerData()), siblings carry the
  // same fields raw from the shared GraphQL shape (native-swatch-
  // shared.js). tag-badges are returned RAW here (still carrying their
  // "Badge:" prefix) for both cases — stripped once, in
  // buildBadgeSpans(), not duplicated per code path.
  function getProductBadgeData(p, currHandle, currTagBadges, currMetafieldBadges, currHasCompareAtPrice) {
    if (p.handle === currHandle) {
      return { tagBadges: currTagBadges, metafieldBadges: currMetafieldBadges, hasCompareAtPrice: currHasCompareAtPrice };
    }
    var tagBadges = (p.tags || []).filter(function (t) { return t.indexOf('Badge:') !== -1; });
    var metafieldBadges = safeJsonParse(p.badgesMetafield && p.badgesMetafield.value, []);
    // Native Shoes (2026-08-31): a genuine price comparison, not "does
    // compareAtPrice exist" — see this file's v3.2.0 changelog and the
    // matching fix in snippets/card-product.liquid for why a bare
    // truthy/existence check is wrong (Shopify's product.
    // compare_at_price in Liquid can be truthy with zero real markdown;
    // this project's own snippets/price.liquid has always compared
    // compare_at_price > price for exactly that reason).
    var range = getSiblingPriceRange(p);
    var hasCompareAtPrice = !!(
      range.compareAtPrice != null && range.price != null &&
      parseFloat(range.compareAtPrice) > parseFloat(range.price)
    );
    return { tagBadges: tagBadges, metafieldBadges: metafieldBadges, hasCompareAtPrice: hasCompareAtPrice };
  }

  // Matches snippets/card-product.liquid's exact badge order: tag
  // badges, then custom.badges metafield badges, then Sale. No sold-out
  // badge here — a sold-out color is never a clickable swatch in the
  // first place (processContainer() already filters those out before
  // render), so there's no stale sold-out state a click could produce.
  function buildBadgeSpans(badgeData, badgeCfg, discount) {
    var frag = document.createDocumentFragment();
    // Native Shoes (2026-10-06): Anniversary Sale badge first, matching
    // snippets/card-product.liquid's own order (it renders the discount
    // badge ahead of the tag/metafield/Sale badges). Card style is the
    // bare "20% OFF" form; the long "NAME - 20% OFF" form is PDP/cart.
    if (discount && discount.eligible) {
      var discountSpan = document.createElement('span');
      discountSpan.className = 'native-discount-badge';
      discountSpan.setAttribute('data-nd-badge', '');
      discountSpan.setAttribute('data-nd-collection', discount.handle);
      discountSpan.setAttribute('data-nd-applied', '1');
      discountSpan.textContent = discount.percent + '% OFF';
      frag.appendChild(discountSpan);
    }
    (badgeData.tagBadges || []).forEach(function (t) {
      var span = document.createElement('span');
      span.textContent = t.split('Badge: ').join('');
      frag.appendChild(span);
    });
    (badgeData.metafieldBadges || []).forEach(function (b) {
      var span = document.createElement('span');
      span.textContent = b;
      frag.appendChild(span);
    });
    if (badgeCfg.showSaleBadges && badgeData.hasCompareAtPrice && badgeCfg.textOnSale) {
      var saleSpan = document.createElement('span');
      saleSpan.textContent = badgeCfg.textOnSale;
      frag.appendChild(saleSpan);
    }
    return frag;
  }

  // Native Shoes (2026-08-31): price sync, see this file's v3.3.0
  // changelog. Standard reference formatMoney() implementation
  // (Shopify's own long-published algorithm — the same one countless
  // themes ship as assets/money_format.js — reimplemented here since
  // this theme doesn't already have one): replaces the one {{amount...}}
  // placeholder in a shop money_format string with a formatted number.
  // Takes an amount in CENTS, matching Shopify's own convention.
  function formatMoney(cents, format) {
    if (typeof cents !== 'number' || isNaN(cents)) return '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || '${{amount}}';
    var match = formatString.match(placeholderRegex);
    if (!match) return formatString;

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = (typeof precision === 'undefined') ? 2 : precision;
      thousands = (typeof thousands === 'undefined') ? ',' : thousands;
      decimal = (typeof decimal === 'undefined') ? '.' : decimal;
      var n = (number / 100).toFixed(precision);
      var parts = n.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      var centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }

    var value;
    switch (match[1]) {
      case 'amount_no_decimals': value = formatWithDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = formatWithDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(cents, 0, '.', ','); break;
      default: value = formatWithDelimiters(cents, 2);
    }
    return formatString.replace(placeholderRegex, value);
  }

  // Native Shoes (2026-10-08): the PDP/PLP price rule, mirrored from snippets/native-money.liquid. United
  // States and Canada: no ".00" (cents only when there are some) and the market's currency code after the
  // price ("$45 USD", "$45.50 CAD"). Every other market keeps the shop's normal format (".00" stays) with the
  // same currency code after it ("$83.00 AUD"). Shopify.country and
  // Shopify.currency.active are the storefront's own market globals, the client-side twin of
  // localization.country.iso_code / .currency.iso_code. Keep this and native-money.liquid in step.
  function formatPrice(cents, moneyCfg) {
    var country = window.Shopify && window.Shopify.country;
    var code = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
    if ((country === 'US' || country === 'CA') && code) {
      var format = moneyCfg.format || '${{amount}}';
      if (typeof cents === 'number' && cents % 100 === 0) {
        format = format.replace(/\{\{\s*amount\s*\}\}/, '{{amount_no_decimals}}')
          .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, '{{amount_no_decimals_with_comma_separator}}');
      }
      var text = formatMoney(cents, format);
      return text ? text + ' ' + code : text;
    }
    if (code) {
      var other = formatMoney(cents, moneyCfg.format || '${{amount}}');
      return other ? other + ' ' + code : other;
    }
    return formatMoney(cents, moneyCfg.currencyCodeEnabled ? moneyCfg.formatWithCurrency : moneyCfg.format);
  }

  function moneyFromAmountString(amountStr, moneyCfg) {
    var amount = parseFloat(amountStr);
    if (isNaN(amount)) return '';
    return formatPrice(Math.round(amount * 100), moneyCfg);
  }

  // Mirrors getProductImages()/getProductBadgeData()'s current-vs-
  // sibling split. price/compareAtPrice/priceVaries/compareAtPriceVaries
  // for the CURRENT product come down as Liquid-rendered data
  // attributes (the exact same values snippets/price.liquid itself
  // used to server-render this card's price block); siblings are
  // computed from the shared GraphQL shape's variants(first:100) list
  // (already fetched for the badge fix above) — min/max price and
  // compare_at_price across all variants approximate product.
  // price_varies/compare_at_price_varies closely enough for a card
  // display (this catalog's real-world variants are same-priced by
  // size in the overwhelming majority of cases).
  function getProductPriceData(p, currHandle, currPriceData) {
    if (p.handle === currHandle) return currPriceData;
    return getSiblingPriceRange(p);
  }

  // ─── Anniversary Sale (2026-10-06) ───────────────────────────────────
  // v3.4.0 — Same class of bug the v3.2.0 badge fix and v3.3.0 price fix
  // addressed, for the tiered Anniversary Sale discount: clicking a
  // sibling swatch left whatever the ORIGINAL server-rendered color's
  // discount state was in place, so a 30%-off color could show the
  // 20%-off color's badge and price (or none at all). Originally mirrored
  // snippets/active-discount.liquid's rule against a
  // collections{discount_* metafields} block fetched per sibling over
  // GraphQL.
  //
  // v3.5.0 — URGENT REVERT (2026-10-06, same day): that per-sibling
  // metafields fetch (up to 250 siblings x 50 collections x 5 metafields
  // each) almost certainly exceeded the Storefront API's query-cost
  // budget — live, reported symptom was the shared identity query
  // erroring out sitewide, swatches collapsing to "current color only,"
  // plus real added latency even when it didn't outright error. See
  // assets/native-swatch-shared.js's matching v1.6.0 changelog for the
  // full root cause.
  //
  // Fix: computeDiscount() no longer reads metafields off a sibling's
  // collections at all (that sub-query is gone from the shared GraphQL
  // shape — siblings now carry only collection HANDLES, cheap, no sub-
  // query). Instead it checks each handle against discountTiers, a small
  // lookup table Liquid builds ONCE from the 4 known tier collection
  // handles (snippets/native-collection-swatches.liquid's
  // data-active-discount-tiers attribute, parsed in readContainerData()
  // below) — only tiers already confirmed active + in-window server-side
  // appear in that table at all, so no date-window check is needed here
  // either; a handle simply not being a key in the table means "no active
  // discount for this sibling," same end result as before.

  // Eligibility is the native_viewed_<handle> cookie (delegated to
  // assets/native-anniversary-sale.js, the single owner of that cookie
  // contract, with a local fallback if it hasn't booted yet).
  function hasViewedCookie(handle) {
    if (!handle) return false;
    if (window.NativeAnniversarySale && window.NativeAnniversarySale.isEligible) {
      return window.NativeAnniversarySale.isEligible(handle);
    }
    // Boundary-anchored, not a raw substring search — 'native_viewed_foo=1'
    // is also a substring of 'native_viewed_foo=10', so a plain indexOf
    // would false-positive if the cookie's value were ever anything other
    // than exactly "1". Split into real cookie pairs and compare the value
    // exactly instead.
    var pairs = document.cookie.split(';');
    var target = 'native_viewed_' + handle;
    for (var i = 0; i < pairs.length; i++) {
      var eq = pairs[i].indexOf('=');
      if (eq === -1) continue;
      if (pairs[i].slice(0, eq).trim() === target) {
        return pairs[i].slice(eq + 1).trim() === '1';
      }
    }
    return false;
  }

  // discountMap is now keyed by PRODUCT handle (not collection handle),
  // built ONCE per page load by assets/native-swatch-shared.js's
  // fetchActiveDiscountMap() — a flat query of the 4 known tier
  // collections' own product lists, done once regardless of how many
  // styles/cards are on the page. This replaces the earlier approach of
  // asking each sibling for its OWN collections(): that field lived
  // inside the per-style references(first:250) loop, so its cost was
  // MULTIPLICATIVE (siblings x collections) no matter how small the
  // per-sibling cap was — confirmed live, still broke after 10->50.
  // Looking a sibling up by its own handle directly is O(1), flat, and
  // can't miss a match the way a truncated per-sibling fetch could.
  function computeDiscount(p, discountMap) {
    var productHandle = p && p.handle;
    var tier = productHandle && discountMap && discountMap[productHandle];
    if (!tier) return null;

    // NOTE: `handle` here must stay the TIER COLLECTION's handle, not the
    // product's — hasViewedCookie()/the data-nd-collection attribute/the
    // native_viewed_<handle> cookie contract are all keyed by collection
    // handle. productHandle was only ever the lookup key into discountMap.
    var best = {
      percent: tier.pct,
      name: tier.name || 'On sale',
      handle: tier.collectionHandle
    };
    best.eligible = hasViewedCookie(best.handle);
    return best;
  }

  // Native Shoes (2026-10-06, fix): ALWAYS trust the Liquid-rendered
  // currDiscount for the current/isCurrent product, same unconditional
  // current-vs-sibling split every other getProduct*() helper in this file
  // uses (getProductImages/getProductBadgeData/getProductPriceData all do
  // `if (p.handle === currHandle) return curr...` with no further
  // condition). This USED to special-case "but if the identity fetch's own
  // node for this product carries real collections data, recompute from
  // that instead" — reasoning being that data should be equivalent to the
  // Liquid-computed value. It isn't guaranteed to be: the shared GraphQL
  // shape fetches only collections(first: 10) per product (assets/native-
  // swatch-shared.js), with no guaranteed ordering, while Liquid's
  // ad_product.collections (snippets/card-product.liquid /
  // native-collection-swatches.liquid) iterates the product's COMPLETE,
  // unrestricted collection list. A product belonging to more than 10
  // collections, with its Anniversary Sale tier collection landing outside
  // whichever 10 GraphQL happens to return, would silently compute "no
  // discount" for its own current color — live-reproduced bug: clicking a
  // sibling swatch away and back never restored the original color's own
  // badge/strikethrough, because the client-recomputed discount (from a
  // possibly-truncated collections list) came back null even though the
  // page had just server-rendered that exact discount correctly. Siblings
  // still need computeDiscount()'s client-side table lookup (they have no
  // Liquid-rendered discount data at all), so only the current-product
  // branch changes here.
  function getProductDiscount(p, currHandle, currDiscount, discountTiers) {
    if (p.handle === currHandle) return currDiscount;
    return computeDiscount(p, discountTiers);
  }

  // Cards whose server-rendered color had no active discount ship with
  // no discount markup at all (snippets/card-product.liquid renders the
  // plain {% render 'price' %} path, byte-identical to before this
  // feature existed). A sibling that DOES have one therefore needs the
  // nodes created here — matching snippets/active-discount-price.liquid's
  // markup exactly so component-price.css's .price--on-sale rules apply.
  function ensureDiscountNodes(card) {
    var scope = card.querySelector('.card-product--price');
    if (!scope) return null;
    scope.setAttribute('data-nd-scope', '');

    var regular = scope.querySelector('[data-nd-regular]');
    if (!regular) {
      var priceEl = scope.querySelector('.price');
      if (!priceEl) return null;
      regular = document.createElement('div');
      regular.setAttribute('data-nd-regular', '');
      scope.insertBefore(regular, priceEl);
      regular.appendChild(priceEl);
    }

    var block = scope.querySelector('[data-nd-price]');
    if (!block) {
      block = document.createElement('div');
      block.className = 'price price--on-sale native-discount-price hidden';
      block.setAttribute('data-nd-price', '');
      block.innerHTML =
        '<div class="price__container">' +
          '<div class="price__regular"><span class="price-item price-item--regular"></span></div>' +
          '<div class="price__sale">' +
            '<span aria-hidden="true"><s class="price-item price-item--regular"></s></span>' +
            '<span class="price-item price-item--sale price-item--last" aria-hidden="true"></span>' +
          '</div>' +
        '</div>';
      scope.appendChild(block);
    }

    return { scope: scope, regular: regular, block: block };
  }

  function updateCardDiscount(card, discount, priceData, moneyCfg) {
    var nodes = ensureDiscountNodes(card);
    if (!nodes) return;

    var show = !!(discount && discount.eligible && priceData && priceData.price != null);
    if (!show) {
      nodes.block.classList.add('hidden');
      nodes.block.removeAttribute('data-nd-applied');
      // Also clear data-nd-collection (not just -applied) — leaving it set
      // meant assets/native-anniversary-sale.js's MutationObserver (which
      // matches on [data-nd-collection]:not([data-nd-applied])) would
      // re-reveal THIS now-stale, previously-built sale price the next time
      // it ran, even though the shopper just switched to a non-qualifying
      // color. Confirmed via code review: real bug, not theoretical.
      nodes.block.removeAttribute('data-nd-collection');
      nodes.regular.classList.remove('hidden');
      return;
    }

    var cents = Math.round(parseFloat(priceData.price) * 100);
    var saleCents = Math.round((cents * (100 - discount.percent)) / 100);
    var saleMoney = formatPrice(saleCents, moneyCfg);
    var compareMoney = formatPrice(cents, moneyCfg);

    var regularItem = nodes.block.querySelector('.price__regular .price-item--regular');
    var compareItem = nodes.block.querySelector('.price__sale s.price-item--regular');
    var saleItem = nodes.block.querySelector('.price__sale .price-item--sale');
    if (regularItem) regularItem.textContent = saleMoney;
    if (compareItem) compareItem.textContent = compareMoney;
    if (saleItem) saleItem.textContent = saleMoney;

    nodes.block.setAttribute('data-nd-collection', discount.handle);
    nodes.block.setAttribute('data-nd-applied', '1');
    nodes.block.setAttribute(
      'aria-label',
      'Regular price ' + compareMoney + ', now ' + saleMoney + ', ' + discount.percent + '% off.'
    );
    nodes.block.classList.remove('hidden');
    nodes.regular.classList.add('hidden');
  }

  // Matches snippets/price.liquid's own card-context output exactly
  // (target: product, no use_variant/show_badges/show_discounts — a
  // card's render call never passes those, confirmed in snippets/
  // card-product.liquid, so the discount-badge and unit-price blocks
  // never apply here and aren't replicated).
  function updateCardPrice(card, priceData, moneyCfg) {
    var priceWrap = card.querySelector('.card-product--price .price');
    if (!priceWrap || !priceData || priceData.price == null) return;

    var hasCompareAtPrice = priceData.compareAtPrice != null && parseFloat(priceData.compareAtPrice) > parseFloat(priceData.price);
    priceWrap.classList.toggle('price--on-sale', hasCompareAtPrice);
    priceWrap.classList.toggle('price--no-compare', priceData.priceVaries === false && priceData.compareAtPriceVaries === true);

    var moneyPrice = moneyFromAmountString(priceData.price, moneyCfg);

    var regularItem = priceWrap.querySelector('.price__regular .price-item--regular');
    if (regularItem) regularItem.textContent = moneyPrice;

    var saleWrap = priceWrap.querySelector('.price__sale');
    if (saleWrap) {
      var compareEl = saleWrap.querySelector('s.price-item--regular');
      var showCompare = !(priceData.priceVaries === false && priceData.compareAtPriceVaries === true);
      if (compareEl) {
        compareEl.parentElement.style.display = showCompare ? '' : 'none';
        if (hasCompareAtPrice) compareEl.textContent = moneyFromAmountString(priceData.compareAtPrice, moneyCfg);
      }
      var saleItem = saleWrap.querySelector('.price-item--sale');
      if (saleItem) saleItem.textContent = moneyPrice;
    }
  }

  function findCardImages(card) {
    var cardMedia = card.querySelector('.card-media');
    var root = cardMedia || card;

    var primary = root.querySelector('.product-card-default-image img') ||
                  root.querySelector('.card-product--single-image-wrapper img') ||
                  root.querySelector('.card-product--image-wrapper img');

    var secondaryEl  = root.querySelector('.product-card-reveal-image img');
    var secondaryWrap = secondaryEl ? secondaryEl.closest('.product-card-reveal-image') : null;

    return {
      primary: primary,
      secondary: secondaryEl,
      secondaryWrap: secondaryWrap,
      origPrimarySrc:    primary ? primary.getAttribute('src') || '' : '',
      origPrimarySrcset: primary ? primary.getAttribute('srcset') || '' : '',
      origPrimaryAlt:    primary ? primary.getAttribute('alt') || '' : '',
      origSrc:        secondaryEl ? secondaryEl.getAttribute('src') || '' : '',
      origSrcset:     secondaryEl ? secondaryEl.getAttribute('srcset') || '' : '',
      origAlt:        secondaryEl ? secondaryEl.getAttribute('alt') || '' : '',
      origVisibility: secondaryWrap ? (secondaryWrap.style.visibility || '') : ''
    };
  }

  function handleSwatchClick(container, card, clickedBtn, productPath, imgData, cardImgs, imgRatio, badgeData, badgeCfg, priceData, moneyCfg, isCurrent, discount) {
    container.querySelectorAll('.ncs-swatch-btn').forEach(function (b) {
      b.classList.remove('is-selected');
      b.setAttribute('aria-pressed', 'false');
    });
    clickedBtn.classList.add('is-selected');
    clickedBtn.setAttribute('aria-pressed', 'true');

    if (!card) return;

    card.classList.toggle('card-product--has-shadow', !!imgData.hasDropShadow);

    // Native Shoes (2026-08-31): dynamic badging — rebuild the badge row
    // for whichever color just got selected, matching snippets/card-
    // product.liquid's own badge markup exactly. Only the inner spans
    // are replaced; the wrapper div's own classes (pc-badges-{position},
    // pc-badges-rounded-radius) are theme-wide settings, not per-
    // product, so they're already correct and left untouched.
    var badgesWrap = card.querySelector('.card-product--badges');
    if (badgesWrap && badgeData && badgeCfg) {
      badgesWrap.innerHTML = '';
      badgesWrap.appendChild(buildBadgeSpans(badgeData, badgeCfg, discount));
    }

    // Native Shoes (2026-08-31): price sync — same fix, same reason, for
    // the price block instead of the badge row. See this file's v3.3.0
    // changelog and updateCardPrice()'s own comment.
    if (priceData && moneyCfg) {
      updateCardPrice(card, priceData, moneyCfg);
      // Native Shoes (2026-10-06): Anniversary Sale — must run AFTER
      // updateCardPrice(), which rewrites the regular/MSRP block this
      // one then strikes through and overlays. See v3.4.0 above.
      updateCardDiscount(card, discount, priceData, moneyCfg);
    }

    var imgs = cardImgs;

    if (imgs.primary && imgData.img1) {
      if (isCurrent && imgs.origPrimarySrc) {
        // Native Shoes (2026-10-05): clicking back to the card's own
        // original swatch shouldn't recompute a fresh CDN URL — that
        // string can differ from whatever size the page originally
        // rendered, forcing a real re-fetch even though this exact
        // image already painted on load. Restore the untouched original
        // attributes instead, same pattern as the secondary image below.
        imgs.primary.src    = imgs.origPrimarySrc;
        imgs.primary.srcset = imgs.origPrimarySrcset || '';
        imgs.primary.alt    = imgs.origPrimaryAlt || '';
      } else {
        imgs.primary.src    = buildSizedUrl(imgData.img1, 800, 0);
        imgs.primary.srcset = buildSrcset(imgData.img1, imgRatio);
        imgs.primary.alt    = imgData.alt1 || '';
      }
    }

    if (imgs.secondary) {
      if (imgData.img2) {
        imgs.secondary.src    = buildSizedUrl(imgData.img2, 800, 0);
        imgs.secondary.srcset = buildSrcset(imgData.img2, imgRatio);
        imgs.secondary.alt    = imgData.alt2 || '';
        if (imgs.secondaryWrap) imgs.secondaryWrap.style.visibility = '';
      } else if (imgs.origSrc) {
        imgs.secondary.src    = imgs.origSrc;
        imgs.secondary.srcset = imgs.origSrcset || '';
        imgs.secondary.alt    = imgs.origAlt || '';
        if (imgs.secondaryWrap) imgs.secondaryWrap.style.visibility = imgs.origVisibility;
      } else if (imgs.secondaryWrap) {
        imgs.secondaryWrap.style.visibility = 'hidden';
      }
    }

    if (productPath) {
      card.querySelectorAll('a[href]').forEach(function (a) {
        if ((a.getAttribute('href') || '').indexOf('/products/') !== -1) {
          a.setAttribute('href', productPath);
        }
      });
    }
    log('swatch click ->', productPath);
  }

  function renderSwatches(container, card, cardImgs, visible, filteredTotal, currHandle, currUrl, swatchMap, currImg1, currAlt1, currImg2, currAlt2, collectionQuery, currHasDropShadow, imgRatio, currTagBadges, currMetafieldBadges, currHasCompareAtPrice, badgeCfg, currPriceData, moneyCfg, currDiscount, discountTiers) {
    container.innerHTML = '';

    var row = document.createElement('div');
    row.className = 'ncs-row';

    var list = document.createElement('ul');
    list.className = 'ncs-strip';
    list.setAttribute('role', 'list');

    visible.forEach(function (p) {
      var isCurrent = (p.handle === currHandle);
      var productPath = isCurrent
        ? buildSwatchUrl(currUrl.split('?')[0], collectionQuery)
        : buildSwatchUrl(getProductPath(p), collectionQuery);
      var imgData = getProductImages(p, currImg1, currAlt1, currImg2, currAlt2, currHandle, currHasDropShadow);
      var badgeData = getProductBadgeData(p, currHandle, currTagBadges, currMetafieldBadges, currHasCompareAtPrice);
      var priceData = getProductPriceData(p, currHandle, currPriceData);
      var discount = getProductDiscount(p, currHandle, currDiscount, discountTiers);

      var colorHandle = p.colorMetafield && p.colorMetafield.value;
      var swatch = (colorHandle && swatchMap && swatchMap[colorHandle]) || {};
      var colorName = (swatch.color_name && swatch.color_name.value) || p.handle;

      var li = document.createElement('li');
      li.className = 'ncs-swatch-item';

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ncs-swatch-btn' + (isCurrent ? ' is-selected' : '');
      btn.setAttribute('aria-pressed', isCurrent ? 'true' : 'false');
      btn.setAttribute('aria-label', colorName);
      btn.setAttribute('title', colorName);
      btn.appendChild(buildDot(p, swatchMap));

      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        handleSwatchClick(container, card, btn, productPath, imgData, cardImgs, imgRatio, badgeData, badgeCfg, priceData, moneyCfg, isCurrent, discount);
      });

      // Native Shoes (2026-10-05): swatch buttons have no real href, so
      // the global hover-prefetch listener (assets/hover-prefetch.js)
      // never matches them. Hovering a swatch is a strong navigation
      // signal though, so request a prefetch for that color's product
      // path directly via the module's public hook, and warm the image
      // cache for its featured image at the same size handleSwatchClick
      // will request (800w) so the hero image swap is instant too.
      var prefetchTimer = null;
      btn.addEventListener('mouseenter', function () {
        prefetchTimer = setTimeout(function () {
          if (window.NativePrefetch && productPath) {
            window.NativePrefetch.request(productPath);
          }
          if (imgData.img1 && !imagePrefetched.has(imgData.img1)) {
            imagePrefetched.add(imgData.img1);
            var preloadImg = new Image();
            preloadImg.src = buildSizedUrl(imgData.img1, 800, 0);
          }
        }, 65);
      });
      btn.addEventListener('mouseleave', function () {
        if (prefetchTimer) {
          clearTimeout(prefetchTimer);
          prefetchTimer = null;
        }
      });

      li.appendChild(btn);
      list.appendChild(li);
    });

    row.appendChild(list);

    var remaining = filteredTotal - visible.length;
    if (remaining > 0) {
      var moreLink = document.createElement('a');
      moreLink.className = 'ncs-more-btn';
      moreLink.href = buildSwatchUrl(currUrl.split('?')[0], collectionQuery);
      moreLink.setAttribute('aria-label', 'View all ' + filteredTotal + ' colors');
      var line1 = document.createElement('span');
      line1.textContent = '+ ' + remaining;
      var line2 = document.createElement('span');
      line2.textContent = 'more';
      moreLink.appendChild(line1);
      moreLink.appendChild(line2);
      row.appendChild(moreLink);
    }

    container.appendChild(row);
    container.classList.remove('ncs--loading');
    container.removeAttribute('aria-busy');
    log('rendered', visible.length, 'swatches +', remaining, 'more | handle:', currHandle);
  }

  // Native Shoes (2026-08-31): tolerant JSON.parse — a malformed/blank
  // data attribute should degrade to "no badges of this kind", never
  // throw and break the whole swatch row.
  function safeJsonParse(raw, fallback) {
    if (!raw) return fallback;
    try {
      var parsed = JSON.parse(raw);
      return parsed == null ? fallback : parsed;
    } catch (e) {
      return fallback;
    }
  }

  // Native Shoes (2026-10-06): same base64 payload decode as
  // assets/native-anniversary-sale.js's decodePayload() — duplicated
  // rather than shared, since this file and that one load independently
  // and neither currently has a shared-utility entry point for this.
  // Tolerant: malformed/missing payload degrades to null, never throws.
  function decodeDiscountPayload(raw) {
    if (!raw) return null;
    try {
      var json = window.atob(raw);
      try { json = decodeURIComponent(escape(json)); } catch (e2) {}
      return JSON.parse(json);
    } catch (e) {
      return null;
    }
  }

  function readContainerData(container) {
    return {
      token:        container.dataset.storefrontToken || '',
      domain:       container.dataset.shopDomain || '',
      styleHandle:  (container.dataset.styleHandle || '').toLowerCase(),
      currHandle:   container.dataset.currentProductHandle || '',
      currUrl:      container.dataset.currentProductUrl || '',
      currColor:    (container.dataset.currentSwatchColor || '').toLowerCase(),
      currImg1:     container.dataset.currentProductImage || '',
      currAlt1:     container.dataset.currentProductImageAlt || '',
      currImg2:     container.dataset.currentProductImage2 || '',
      currAlt2:     container.dataset.currentProductImage2Alt || '',
      currHasDropShadow: container.dataset.currentHasDropShadow === 'true',
      imgRatio:     parseFloat(container.dataset.cardImgHeightRatio) || 0.82,
      // Native Shoes (2026-08-31): dynamic badging — see this file's
      // v3.2.0 changelog. currTagBadges/currMetafieldBadges are RAW
      // (tag-badges still carry their "Badge:" prefix, stripped later
      // in buildBadgeSpans() — same place siblings get stripped, one
      // code path instead of two).
      badgeCfg: {
        showSaleBadges: container.dataset.showSaleBadges === 'true',
        textOnSale: container.dataset.textOnSale || ''
      },
      currTagBadges:        safeJsonParse(container.dataset.currentTagBadges, []),
      currMetafieldBadges:  safeJsonParse(container.dataset.currentMetafieldBadges, []),
      currHasCompareAtPrice: container.dataset.currentHasCompareAtPrice === 'true',
      // Native Shoes (2026-08-31): price sync — see this file's v3.3.0
      // changelog and updateCardPrice()'s own comment.
      currPriceData: {
        price: container.dataset.currentPrice || null,
        compareAtPrice: container.dataset.currentCompareAtPrice || null,
        priceVaries: container.dataset.currentPriceVaries === 'true',
        compareAtPriceVaries: container.dataset.currentCompareAtPriceVaries === 'true'
      },
      // Native Shoes (2026-10-06): Anniversary Sale.
      // currDiscount is only ever used for the synthetic fallback object
      // built when the current color is missing from the identity list —
      // every real identity product carries its own collections data.
      currDiscount: (function () {
        var payload = decodeDiscountPayload(container.dataset.currentDiscountPayload);
        if (!payload) return null;
        var handle = container.dataset.currentDiscountHandle || '';
        return {
          percent: parseInt(payload.pct, 10) || 0,
          name: payload.name || 'On sale',
          handle: handle,
          eligible: hasViewedCookie(handle)
        };
      })(),
      // Native Shoes (2026-10-06, URGENT FIX): small lookup table of
      // currently-active Anniversary Sale tiers, built once server-side
      // (snippets/native-collection-swatches.liquid) from the 4 known
      // tier collection handles — replaces the per-sibling GraphQL
      // metafields fetch that blew the Storefront API's query-cost
      // budget. Parsed here into an object keyed by collection handle so
      // computeDiscount() can do a cheap lookup per sibling handle
      // instead of a metafield scan. safeJsonParse degrades a malformed/
      // missing attribute to [] (no active tiers), never throws.
      discountTiers: (function () {
        var arr = safeJsonParse(container.dataset.activeDiscountTiers, []);
        var map = {};
        (arr || []).forEach(function (t) {
          if (t && t.handle) map[t.handle] = t;
        });
        return map;
      })(),
      moneyCfg: {
        format: container.dataset.moneyFormat || '${{amount}}',
        formatWithCurrency: container.dataset.moneyFormatWithCurrency || '${{amount}} USD',
        currencyCodeEnabled: container.dataset.currencyCodeEnabled === 'true'
      }
    };
  }

  // No style at all — nothing to fetch for siblings, and this exact product
  // is already known available (it's on the page). Only network needed is
  // this one color's own swatch image, via the shared, persisted swatch
  // cache — usually already warm from some other card on the same page or
  // an earlier visit.
  function renderSelfOnly(container, discountMap) {
    var d = readContainerData(container);
    var tiers = discountMap || d.discountTiers;
    var card = container.closest('[data-product-handle]');
    var cardImgs = card ? findCardImages(card) : {
      primary: null, secondary: null, secondaryWrap: null,
      origSrc: '', origSrcset: '', origAlt: '', origVisibility: ''
    };

    var selfProduct = {
      handle: d.currHandle,
      onlineStoreUrl: d.currUrl,
      featuredImage: d.currImg1 ? { url: d.currImg1, altText: d.currAlt1 } : null,
      media: { nodes: [] },
      colorMetafield: d.currColor ? { value: d.currColor } : null
    };

    if (!d.currColor || !d.token) {
      renderSwatches(container, card, cardImgs, [selfProduct], 1, d.currHandle, d.currUrl, {}, d.currImg1, d.currAlt1, d.currImg2, d.currAlt2, getCollectionQuery(), d.currHasDropShadow, d.imgRatio, d.currTagBadges, d.currMetafieldBadges, d.currHasCompareAtPrice, d.badgeCfg, d.currPriceData, d.moneyCfg, d.currDiscount, tiers);
      return;
    }

    window.NativeShops.swatchShared.fetchSwatches(d.domain, d.token, [d.currColor]).then(function (swatchMap) {
      renderSwatches(container, card, cardImgs, [selfProduct], 1, d.currHandle, d.currUrl, swatchMap, d.currImg1, d.currAlt1, d.currImg2, d.currAlt2, getCollectionQuery(), d.currHasDropShadow, d.imgRatio, d.currTagBadges, d.currMetafieldBadges, d.currHasCompareAtPrice, d.badgeCfg, d.currPriceData, d.moneyCfg, d.currDiscount, tiers);
    });
  }

  function processContainer(container, identity, availMap, discountMap) {
    var d = readContainerData(container);
    var tiers = discountMap || d.discountTiers;
    var card = container.closest('[data-product-handle]');
    var cardImgs = card ? findCardImages(card) : {
      primary: null, secondary: null, secondaryWrap: null,
      origSrc: '', origSrcset: '', origAlt: '', origVisibility: ''
    };
    if (card) {
      if (!d.currImg2 && cardImgs.origSrc) d.currImg2 = cardImgs.origSrc.split('?')[0];
      if (!d.currAlt2 && cardImgs.origAlt) d.currAlt2 = cardImgs.origAlt;
    }

    var products = (identity && identity.products) || [];
    var swatchMap = (identity && identity.swatchMap) || {};

    // Availability decides EVERYTHING here — never anything from the
    // cached identity fetch, which no longer even carries this field.
    var filtered = products.filter(function (p) {
      if (!p || !p.handle) return false;
      if (p.handle === d.currHandle) return true; // already known available
      return availMap[p.handle] === true;
    });

    var currentIdx = -1;
    filtered.forEach(function (p, i) { if (p.handle === d.currHandle) currentIdx = i; });

    var ordered = [];
    if (currentIdx === -1) {
      ordered.push({
        handle: d.currHandle,
        onlineStoreUrl: d.currUrl,
        featuredImage: d.currImg1 ? { url: d.currImg1, altText: d.currAlt1 } : null,
        media: { nodes: [] },
        colorMetafield: d.currColor ? { value: d.currColor } : null
      });
      filtered.forEach(function (p) { ordered.push(p); });
    } else {
      ordered.push(filtered[currentIdx]);
      filtered.forEach(function (p, i) { if (i !== currentIdx) ordered.push(p); });
    }

    if (ordered.length <= 1) {
      renderSelfOnly(container, discountMap);
      return;
    }

    renderSwatches(
      container, card, cardImgs,
      ordered.slice(0, SWATCH_LIMIT), ordered.length,
      d.currHandle, d.currUrl, swatchMap,
      d.currImg1, d.currAlt1, d.currImg2, d.currAlt2,
      getCollectionQuery(), d.currHasDropShadow, d.imgRatio,
      d.currTagBadges, d.currMetafieldBadges, d.currHasCompareAtPrice, d.badgeCfg,
      d.currPriceData, d.moneyCfg, d.currDiscount, tiers
    );
  }

  // ─── Boot: resolve every distinct style's identity, THEN fire one shared
  // live-availability request covering every handle needed across the
  // whole page, THEN render every card together. No card renders before
  // both are ready — no partial/staggered reveal. ───────────────────────
  function boot() {
    var containers = document.querySelectorAll('[data-native-card-swatches]:not([data-ncs-ready])');
    if (!containers.length) return;
    log('boot —', containers.length, 'containers found');
    containers.forEach(function (c) { c.setAttribute('data-ncs-ready', '1'); });

    var byStyle = {};
    var selfOnly = [];
    var token = '', domain = '';

    containers.forEach(function (c) {
      var d = readContainerData(c);
      if (!token && d.token) token = d.token;
      if (!domain && d.domain) domain = d.domain;
      if (!d.styleHandle) { selfOnly.push(c); return; }
      if (!byStyle[d.styleHandle]) byStyle[d.styleHandle] = [];
      byStyle[d.styleHandle].push(c);
    });

    var discountMapPromise = window.NativeShops.swatchShared.fetchActiveDiscountMap
      ? window.NativeShops.swatchShared.fetchActiveDiscountMap(domain, token)
      : Promise.resolve({});

    selfOnly.forEach(function (c) {
      discountMapPromise.then(function (m) { renderSelfOnly(c, m); });
    });

    var styleHandles = Object.keys(byStyle);
    if (!styleHandles.length) return;

    if (!token || !domain) {
      warn('HIDE — missing storefront token/domain for', styleHandles.length, 'styles');
      styleHandles.forEach(function (sh) {
        byStyle[sh].forEach(function (c) { c.classList.add('native-card-swatches--hidden'); });
      });
      return;
    }

    // discountMapPromise already kicked off above (shared/memoized fetch,
    // reused here rather than fired again) — resolved in parallel with
    // identity resolution so cards never render before either is ready.
    Promise.all([
      Promise.all(styleHandles.map(function (sh) { return window.NativeShops.swatchShared.getStyleIdentity(domain, token, sh); })),
      discountMapPromise
    ]).then(function (results) {
      var identities = results[0];
      var discountMap = results[1] || {};
      var allHandles = {};
      identities.forEach(function (identity) {
        if (!identity) return;
        identity.products.forEach(function (p) { if (p && p.handle) allHandles[p.handle] = true; });
      });
      var handleList = Object.keys(allHandles);
      log('resolved identity for', styleHandles.length, 'styles —', handleList.length, 'total sibling handles to live-check');

      fetchLiveAvailability(domain, token, handleList).then(function (availMap) {
        styleHandles.forEach(function (sh, i) {
          var identity = identities[i];
          var cs = byStyle[sh];
          if (!identity) {
            cs.forEach(function (c) { renderSelfOnly(c, discountMap); });
            return;
          }
          cs.forEach(function (c) { processContainer(c, identity, availMap, discountMap); });
        });
      });
    }).catch(function (e) {
      // Fail closed, matching the availability guarantee above: if identity
      // resolution or the live-availability request throws for any reason,
      // never leave the row stuck on the skeleton forever — hide it.
      err('boot — unexpected error, hiding affected containers', e);
      styleHandles.forEach(function (sh) {
        byStyle[sh].forEach(function (c) { c.classList.add('native-card-swatches--hidden'); });
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);

  document.addEventListener('native:grid:updated', function () {
    log('native:grid:updated — re-booting swatches on filtered/scrolled grid');
    boot();
  });
})();
